import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import shellStyles from '../components/shell/shell.module.css';
import ui from '../styles/ui.module.css';

const STATUS_LABEL: Record<string, string> = { active: 'Ativo', suspended: 'Suspenso' };
const STATUS_PILL: Record<string, string> = { active: 'pillPos', suspended: 'pillDanger' };
const RISK_LABEL: Record<string, string> = { normal: 'Normal', watch: 'Observação', high: 'Alto' };
const RISK_PILL: Record<string, string> = { normal: 'pillMuted', watch: 'pillWarn', high: 'pillDanger' };
const STORE_STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  published: 'Publicada',
  unpublished: 'Despublicada',
  suspended: 'Suspensa',
};

function money(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function when(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

const SIZE = 20;

/** Admin · Parceiros — lista com busca (nome/e-mail/subdomínio) + drill-down em `/parceiros/:id`.
 *  Consome `GET /api/admin/partners`. */
export function Parceiros() {
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(0);
  const partners = useQuery({
    queryKey: ['admin', 'partners', term, page],
    queryFn: () => api.partners({ search: term || undefined, page, size: SIZE }),
  });

  return (
    <>
      <div className={shellStyles.head}>
        <h1>Parceiros</h1>
        <p>Todo parceiro cadastrado na plataforma, com a loja e o GMV de cada um.</p>
      </div>

      <form
        className={ui.actions}
        onSubmit={(e) => {
          e.preventDefault();
          setPage(0);
          setTerm(search.trim());
        }}
      >
        <label className={ui.field} style={{ flex: 1, maxWidth: 340 }}>
          Buscar
          <input
            type="search"
            placeholder="Nome, e-mail ou subdomínio"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <button className={`${ui.btn} ${ui.btnGhost}`} type="submit" style={{ alignSelf: 'flex-end' }}>
          Buscar
        </button>
      </form>

      <div className={ui.card}>
        {partners.isLoading ? (
          <p className={ui.empty}>Carregando…</p>
        ) : !partners.data?.length ? (
          <p className={ui.empty}>{page > 0 ? 'Não há mais parceiros.' : 'Nenhum parceiro encontrado.'}</p>
        ) : (
          <div className={ui.tblScroll}>
            <table className={ui.tbl}>
              <thead>
                <tr>
                  <th>Parceiro</th>
                  <th>Loja</th>
                  <th>Status</th>
                  <th>Risco</th>
                  <th className="r">GMV</th>
                </tr>
              </thead>
              <tbody>
                {partners.data.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/parceiros/${p.id}`}>
                        <b>{p.name}</b>
                      </Link>
                      <div className={ui.faint}>{p.email}</div>
                    </td>
                    <td className={ui.muted}>
                      {p.storeId ? (
                        <>
                          {p.storeName}
                          <div className={ui.faint}>
                            {p.subdomain} · {STORE_STATUS_LABEL[p.storeStatus] ?? p.storeStatus}
                          </div>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <span className={`${ui.pill} ${ui[STATUS_PILL[p.status] ?? 'pillMuted']}`}>
                        {STATUS_LABEL[p.status] ?? p.status}
                      </span>
                    </td>
                    <td>
                      <span className={`${ui.pill} ${ui[RISK_PILL[p.risk] ?? 'pillMuted']}`}>
                        {RISK_LABEL[p.risk] ?? p.risk}
                      </span>
                    </td>
                    <td className="r">
                      <span className={ui.money}>{money(p.gmv)}</span>
                      <div className={ui.faint}>desde {when(p.createdAt)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={ui.actions}>
        <button
          className={`${ui.btn} ${ui.btnGhost} ${ui.btnSmall}`}
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          ← Mais recentes
        </button>
        <button
          className={`${ui.btn} ${ui.btnGhost} ${ui.btnSmall}`}
          disabled={(partners.data?.length ?? 0) < SIZE}
          onClick={() => setPage((p) => p + 1)}
        >
          Mais antigos →
        </button>
      </div>
    </>
  );
}
